/**
 * Polygon boolean and offset operations backed by Clipper (clipper-lib, Boost Software License 1.0).
 * Coordinates are scaled to integer micrometres for robust integer arithmetic.
 */
import * as ClipperNs from 'clipper-lib';
import type { Vec } from './vec';
import { type PolygonWithHoles, type Ring, normalizePolygon, ringArea } from './polygon';

// clipper-lib is CommonJS (`module.exports = ClipperLib`); depending on the bundler the API is on the
// namespace itself or on `default`.
const ClipperLib: typeof ClipperNs = ((ClipperNs as unknown as { default?: typeof ClipperNs }).default ?? ClipperNs);

const SCALE = 1000; // mm -> µm

type IntPath = ClipperNs.IntPoint[];

const toPath = (ring: Ring): IntPath => ring.map((p) => ({ X: Math.round(p.x * SCALE), Y: Math.round(p.y * SCALE) }));
const fromPath = (path: IntPath): Ring => path.map((p) => ({ x: p.X / SCALE, y: p.Y / SCALE }));

function polysToPaths(polys: PolygonWithHoles[]): IntPath[] {
  const paths: IntPath[] = [];
  for (const p of polys) {
    paths.push(toPath(p.outer));
    for (const h of p.holes) paths.push(toPath(h));
  }
  return paths;
}

function treeToPolygons(tree: ClipperNs.PolyTree, minArea: number): PolygonWithHoles[] {
  const ex = ClipperLib.JS.PolyTreeToExPolygons(tree);
  const out: PolygonWithHoles[] = [];
  for (const e of ex) {
    const outer = fromPath(e.outer);
    if (outer.length < 3 || ringArea(outer) < minArea) continue;
    const holes = e.holes.map(fromPath).filter((h) => h.length >= 3 && ringArea(h) >= minArea);
    out.push(normalizePolygon({ outer, holes }));
  }
  return out;
}

function execute(
  clipType: ClipperNs.ClipType,
  subject: IntPath[],
  clip: IntPath[],
  fill: ClipperNs.PolyFillType,
  minArea: number,
): PolygonWithHoles[] {
  const c = new ClipperLib.Clipper();
  c.AddPaths(subject, ClipperLib.PolyType.ptSubject, true);
  if (clip.length) c.AddPaths(clip, ClipperLib.PolyType.ptClip, true);
  const tree = new ClipperLib.PolyTree();
  c.Execute(clipType, tree, fill, fill);
  return treeToPolygons(tree, minArea);
}

/**
 * Resolves self intersections and overlapping rings using the even-odd rule
 * (matching how SVG/raster contours with holes are interpreted). Returns zero or more clean polygons.
 */
export function cleanPolygons(polys: PolygonWithHoles[], minArea = 1e-4): PolygonWithHoles[] {
  return execute(ClipperLib.ClipType.ctUnion, polysToPaths(polys), [], ClipperLib.PolyFillType.pftEvenOdd, minArea);
}

/** Cleans a set of raw rings interpreted with the even-odd rule. */
export function polygonsFromRings(rings: Ring[], minArea = 1e-4): PolygonWithHoles[] {
  return execute(ClipperLib.ClipType.ctUnion, rings.map(toPath), [], ClipperLib.PolyFillType.pftEvenOdd, minArea);
}

/** Cleans raw rings with the non-zero winding rule (ring orientation matters). */
export function polygonsFromRingsNonZero(rings: Ring[], minArea = 1e-4): PolygonWithHoles[] {
  return execute(ClipperLib.ClipType.ctUnion, rings.map(toPath), [], ClipperLib.PolyFillType.pftNonZero, minArea);
}

export function unionPolygons(polys: PolygonWithHoles[], minArea = 1e-4): PolygonWithHoles[] {
  // Each polygon is normalized (outer +, holes -) so non-zero fill gives a proper union.
  const paths = polysToPaths(polys.map(normalizePolygon));
  return execute(ClipperLib.ClipType.ctUnion, paths, [], ClipperLib.PolyFillType.pftNonZero, minArea);
}

export function differencePolygons(a: PolygonWithHoles[], b: PolygonWithHoles[], minArea = 1e-4): PolygonWithHoles[] {
  return execute(
    ClipperLib.ClipType.ctDifference,
    polysToPaths(a.map(normalizePolygon)),
    polysToPaths(b.map(normalizePolygon)),
    ClipperLib.PolyFillType.pftNonZero,
    minArea,
  );
}

export function intersectPolygons(a: PolygonWithHoles[], b: PolygonWithHoles[], minArea = 1e-4): PolygonWithHoles[] {
  return execute(
    ClipperLib.ClipType.ctIntersection,
    polysToPaths(a.map(normalizePolygon)),
    polysToPaths(b.map(normalizePolygon)),
    ClipperLib.PolyFillType.pftNonZero,
    minArea,
  );
}

export type JoinKind = 'miter' | 'round' | 'square';

/**
 * Offsets polygons by `delta` mm (positive grows, negative shrinks). Holes move in the opposite
 * direction automatically. May return several polygons when a shape is split by shrinking, or none
 * when it vanishes.
 */
export function offsetPolygons(polys: PolygonWithHoles[], delta: number, join: JoinKind = 'miter', minArea = 1e-4): PolygonWithHoles[] {
  if (Math.abs(delta) < 1e-6) return cleanPolygons(polys, minArea);
  const co = new ClipperLib.ClipperOffset(2, 0.05 * SCALE);
  const jt =
    join === 'round' ? ClipperLib.JoinType.jtRound : join === 'square' ? ClipperLib.JoinType.jtSquare : ClipperLib.JoinType.jtMiter;
  co.AddPaths(polysToPaths(polys.map(normalizePolygon)), jt, ClipperLib.EndType.etClosedPolygon);
  const tree = new ClipperLib.PolyTree();
  co.Execute(tree, delta * SCALE);
  return treeToPolygons(tree, minArea);
}

/** Offsets an open polyline into a closed outline (used to give lines a physical footprint). */
export function offsetPolyline(line: Vec[], halfWidth: number): PolygonWithHoles[] {
  const co = new ClipperLib.ClipperOffset(2, 0.05 * SCALE);
  co.AddPath(toPath(line), ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenRound);
  const tree = new ClipperLib.PolyTree();
  co.Execute(tree, halfWidth * SCALE);
  return treeToPolygons(tree, 1e-6);
}
